import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { useScrollPosition } from '../../hooks/useScrollPosition';
import { useServiceFinder } from '../ui/ServiceFinderContext';

const navLinks = [
  { label: 'Services', path: '/services' },
  { label: 'Resources', path: '/resources' },
  { label: 'Blog', path: '/blog' },
  { label: 'About', path: '/about' },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isScrolled } = useScrollPosition();
  const location = useLocation();
  const { openFinder } = useServiceFinder();

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const isHome = location.pathname === '/' || location.pathname === '/homepage-previous-version';
  const useDarkText = !isHome || isScrolled;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          mobileOpen
            ? 'bg-night-950'
            : isScrolled
              ? 'bg-cream-200/90 backdrop-blur-xl'
              : 'bg-transparent'
        }`}
      >
        <nav className="container-wide relative flex items-center justify-between h-16 sm:h-20">
          <Link to="/" className="flex items-center gap-2 group">
            <span className={`text-xl font-black tracking-tighter uppercase transition-colors duration-500 ${mobileOpen ? 'text-white' : useDarkText ? 'text-night-950' : 'text-white'}`}>
              W<span className="text-volt-500">A</span>S
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-8 absolute left-1/2 -translate-x-1/2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-xs font-bold uppercase tracking-[0.15em] transition-colors duration-500 relative ${
                  useDarkText
                    ? location.pathname.startsWith(link.path)
                      ? 'text-night-950'
                      : 'text-night-400 hover:text-night-950'
                    : location.pathname.startsWith(link.path)
                      ? 'text-white'
                      : 'text-night-300 hover:text-white'
                }`}
              >
                {link.label}
                {location.pathname.startsWith(link.path) && (
                  <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-volt-500" />
                )}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            <button onClick={openFinder} className="btn-primary text-xs py-2.5 px-5">
              Start a project
              <ArrowUpRight size={14} />
            </button>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden p-3 transition-colors duration-500 ${mobileOpen ? 'text-white' : useDarkText ? 'text-night-950' : 'text-white'}`}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 top-16 sm:top-20 bg-night-950 z-40">
          <div className="flex flex-col p-8 gap-2">
            {navLinks.map((link, i) => (
              <Link
                key={link.path}
                to={link.path}
                className="text-3xl font-black uppercase text-white hover:text-volt-500 transition-colors py-3 border-b border-night-800"
                style={{ animationDelay: `${i * 50}ms` }}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-8">
              <button onClick={() => { setMobileOpen(false); openFinder(); }} className="btn-volt w-full text-center">
                Start a project
                <ArrowUpRight size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
